import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Mail, Phone, MapPin, Clock, Send, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import SEOHead from '../components/SEOHead';
import { useLanguage } from '../store/LanguageContext';
import { API_URL } from '../lib/api';

import { BRAND } from '../config/brand';

// v5 blok 12: alle bedrijfsgegevens komen uit één centrale config.
const COMPANY = {
  name:    BRAND.name,
  street:  BRAND.street,
  city:    `${BRAND.postalCode} ${BRAND.city}`,
  country: BRAND.country,
  btw:     BRAND.btw,
  email:   BRAND.email,
  phone:   BRAND.phone,
  hours:   BRAND.hours,
};

export default function ContactPage() {
  const { t, lang } = useLanguage();
  const [searchParams] = useSearchParams();
  const productSlug = searchParams.get('product');

  const [form, setForm] = useState({
    name: '', email: '', phone: '', postal_code: '', subject: '', message: '',
    consent: false,
  });
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  // Prefill subject + message when arriving from a Restpartij card
  // (link is /contact?product=<slug>).
  useEffect(() => {
    if (productSlug) {
      setForm(f => ({
        ...f,
        subject: f.subject || `Interesse in restpartij: ${productSlug}`,
        message: f.message || `Goedendag,\n\nIk heb interesse in de restpartij "${productSlug}". Kunt u mij meer info bezorgen over beschikbaarheid, levering en plaatsing?\n\nMet vriendelijke groet,`,
      }));
    }
  }, [productSlug]);

  // v3.2.95 — Client-side blocklist leeggemaakt op Filip's verzoek
  // (zoals server-side BLOCKED_EMAIL_DOMAINS sinds v3.2.73). 'me.com' (zijn
  // eigen Apple-adres) en 'you.com' werden hierdoor onterecht geweigerd.
  // Alleen format + dubbele punt blijft als sanity-check; de rest is up to
  // the server.
  function validateEmail(email: string): string | null {
    const trimmed = email.trim();
    if (!trimmed) return t('contact.emailRequired');
    if (!/^[^\s@]+@[^\s@.]+\.[a-zA-Z]{2,}$/.test(trimmed)) return t('contact.emailFormat');
    if (trimmed.includes('..')) return t('contact.emailFormat');
    return null;
  }

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.consent) return;

    // Pre-flight email check — show inline error before hitting the server
    const emailErr = validateEmail(form.email);
    if (emailErr) {
      setStatus('error');
      setErrorMsg(emailErr);
      return;
    }

    setStatus('sending');
    setErrorMsg('');
    try {
      const res = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, language: lang }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: 'Network error' }));
        throw new Error(err.error || 'Submission failed');
      }
      setStatus('success');
      setForm({ name: '', email: '', phone: '', postal_code: '', subject: '', message: '', consent: false });
    } catch (e) {
      setStatus('error');
      setErrorMsg((e as Error).message);
    }
  };

  return (
    <div className="pt-16 md:pt-[72px]">
      <SEOHead page="contact" />

      {/* Hero */}
      <section className="relative h-[160px] md:h-[190px] flex items-center overflow-hidden text-white">
        <div className="absolute inset-0" style={{ backgroundImage: 'url(/products/cottage-ivoor.jpg)', backgroundSize: 'cover', backgroundPosition: 'center' }} />
        <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/60 to-black/35" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#c47a3a]/20 border border-[#c47a3a]/30 rounded-full text-[#c47a3a] text-[11px] font-semibold tracking-wider uppercase mb-3">
            <Mail size={12} /> {t('contact.tag')}
          </div>
          <h1 className="text-2xl md:text-3xl font-bold leading-tight mb-1.5" style={{ fontFamily: 'Playfair Display' }}>
            {t('contact.title')}
          </h1>
          <p className="text-base md:text-lg text-gray-300 max-w-2xl leading-relaxed">
            {t('contact.subtitle')}
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Company info */}
          <aside className="lg:col-span-1 space-y-4">
            <div className="bg-white border border-gray-100 rounded-xl p-6 shadow-sm">
              <h2 className="text-lg font-bold text-[#1a1a1a] mb-4" style={{ fontFamily: 'Playfair Display' }}>
                {COMPANY.name}
              </h2>
              {/* v5 blok 3: e-mail staat centraal — wij nodigen niet uit tot
                   telefoneren. Het telefoonnummer staat (wettelijk verplicht)
                   in de voettekst, niet hier. */}
              <a
                href={`mailto:${COMPANY.email}`}
                className="flex items-center justify-center gap-2 w-full px-4 py-3 bg-[#c47a3a] hover:bg-[#a8643a] text-white rounded-lg text-sm font-bold transition-colors mb-4"
              >
                <Mail size={16} /> {COMPANY.email}
              </a>
              <ul className="space-y-3 text-sm">
                <li className="flex items-start gap-3">
                  <MapPin size={16} className="text-[#c47a3a] flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-[#1a1a1a]">{COMPANY.street}</p>
                    <p className="text-[#1a1a1a]">{COMPANY.city}</p>
                    <p className="text-[#8a7e74] text-xs">{COMPANY.country}</p>
                  </div>
                </li>
                <li className="flex items-center gap-3">
                  <Clock size={16} className="text-[#c47a3a] flex-shrink-0" />
                  <span className="text-[#4a4a4a] text-xs">{COMPANY.hours}</span>
                </li>
              </ul>
              <div className="mt-4 pt-4 border-t border-gray-100 text-xs text-[#8a7e74]">
                {t('footer.btw')}: {COMPANY.btw}
              </div>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
              <h3 className="text-sm font-bold text-amber-900 mb-1">{t('contact.responseTimeTitle')}</h3>
              <p className="text-xs text-amber-800 leading-relaxed">{t('contact.responseTimeDesc')}</p>
            </div>
          </aside>

          {/* Form */}
          <div className="lg:col-span-2">
            <form onSubmit={submit} className="bg-white border border-gray-100 rounded-xl p-6 md:p-8 shadow-sm">
              <h2 className="text-lg font-bold text-[#1a1a1a] mb-1" style={{ fontFamily: 'Playfair Display' }}>
                {t('contact.formTitle')}
              </h2>
              <p className="text-xs text-[#8a7e74] mb-6">{t('contact.formDesc')}</p>

              {status === 'success' && (
                <div className="mb-5 bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
                  <CheckCircle2 size={20} className="text-green-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-green-900 font-semibold text-sm">{t('contact.successTitle')}</p>
                    <p className="text-green-800 text-xs mt-0.5">{t('contact.successDesc')}</p>
                  </div>
                </div>
              )}

              {status === 'error' && (
                <div className="mb-5 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
                  <AlertCircle size={20} className="text-red-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-red-900 font-semibold text-sm">{t('contact.errorTitle')}</p>
                    <p className="text-red-800 text-xs mt-0.5">{errorMsg || t('contact.errorDesc')}</p>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label={t('contact.fName')} required>
                  <input type="text" required minLength={2} value={form.name}
                    onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                    className="form-input" autoComplete="name" />
                </Field>
                <Field label={t('contact.fEmail')} required>
                  <input type="email" required value={form.email}
                    onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                    className="form-input" autoComplete="email" inputMode="email" />
                </Field>
                <Field label={t('contact.fPhone')}>
                  <input type="tel" value={form.phone}
                    onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                    className="form-input" autoComplete="tel" inputMode="tel" />
                </Field>
                <Field label={t('contact.fPostal')} hint={t('contact.fPostalHint')}>
                  <input type="text" value={form.postal_code} maxLength={10}
                    onChange={e => setForm(f => ({ ...f, postal_code: e.target.value }))}
                    className="form-input" autoComplete="postal-code" inputMode="numeric" />
                </Field>
                <div className="sm:col-span-2">
                  <Field label={t('contact.fSubject')}>
                    <input type="text" value={form.subject}
                      onChange={e => setForm(f => ({ ...f, subject: e.target.value }))}
                      className="form-input" />
                  </Field>
                </div>
                <div className="sm:col-span-2">
                  <Field label={t('contact.fMessage')} required>
                    <textarea required minLength={10} rows={6} value={form.message}
                      onChange={e => setForm(f => ({ ...f, message: e.target.value }))}
                      className="form-input resize-y min-h-[120px]" />
                  </Field>
                </div>
                <div className="sm:col-span-2">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input type="checkbox" required checked={form.consent}
                      onChange={e => setForm(f => ({ ...f, consent: e.target.checked }))}
                      className="mt-1 w-4 h-4 text-[#c47a3a] border-gray-300 rounded focus:ring-[#c47a3a]" />
                    <span className="text-xs text-[#4a4a4a] leading-relaxed">
                      {t('contact.consent')}
                    </span>
                  </label>
                </div>
              </div>

              <button
                type="submit"
                disabled={status === 'sending' || !form.consent}
                className="mt-6 w-full sm:w-auto px-6 py-3 bg-[#c47a3a] text-white font-semibold rounded-lg hover:bg-[#a8643a] transition-colors disabled:opacity-50 flex items-center justify-center gap-2 min-h-[44px]"
              >
                {status === 'sending' ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                {status === 'sending' ? t('contact.sending') : t('contact.send')}
              </button>
            </form>
          </div>
        </div>
      </section>

      <style>{`
        .form-input {
          width: 100%;
          padding: 0.625rem 0.875rem;
          border: 1px solid #e5e5e5;
          border-radius: 0.5rem;
          font-size: 0.875rem;
          background-color: #fff;
          transition: border-color 0.15s ease, box-shadow 0.15s ease;
          min-height: 44px; /* iOS-friendly tap target */
        }
        .form-input:focus {
          outline: none;
          border-color: #c47a3a;
          box-shadow: 0 0 0 3px rgba(196, 122, 58, 0.1);
        }
      `}</style>
    </div>
  );
}

function Field({ label, required, hint, children }: { label: string; required?: boolean; hint?: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-[#4a4a4a] uppercase tracking-wide mb-1.5">
        {label}{required && <span className="text-[#c47a3a] ml-1">*</span>}
      </label>
      {children}
      {hint && <p className="text-[10px] text-[#8a7e74] mt-1">{hint}</p>}
    </div>
  );
}
